import { getSavingsViewerKey } from "../Components/Savings/savingsViewer";
import client from "./client";
import { mergeAffiliateAttributionIntoPayload } from "../utils/affiliateAttribution";

const ENDPOINTS = {
  safari: "experience-bookings",
  cruise: "experience-bookings",
  trek: "trek-bookings",
  experience: "experience-bookings",
  room: "room-bookings",
};

const WEB_CHECKIN_ENDPOINTS = {
  safari: "safari-bookings",
  cruise: "cruise-bookings",
  trek: "trek-bookings",
};

const CANCELLATION_ENDPOINTS = {
  safari: "experience-bookings",
  cruise: "cruise-bookings",
  trek: "trek-bookings",
  experience: "experience-bookings",
  room: "room-bookings",
};

const normalizeType = (type) => (ENDPOINTS[type] ? type : "safari");

const normalizeWebCheckinType = (type) => {
  const token = String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (token === "safari" || token === "junglesafari" || token.includes("safari")) return "safari";
  if (token === "cruise" || token.includes("cruise")) return "cruise";
  if (token === "trek" || token === "trail" || token.includes("trek")) return "trek";
  return "";
};

const sanitizeParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined && value !== null && value !== "" && !(typeof value === "string" && value.trim() === ""),
    ),
  );

const buildQueryString = (params = {}) => {
  const query = new URLSearchParams(sanitizeParams(params)).toString();
  return query ? `?${query}` : "";
};

const buildPath = (type, identifier, suffix) => {
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  if (suffix === "bookings") {
    if (!identifier) throw new Error("Experience identifier is required");
    return `${base}/${identifier}/bookings`;
  }
  if (!identifier) {
    throw new Error("Experience identifier is required");
  }
  return `${base}/${identifier}/${suffix}`;
};

export const fetchExperienceAvailability = (type, identifier, params = {}) => {
  const query = buildQueryString(params);
  return client.get(`${buildPath(type, identifier, "availability")}${query}`);
};

export const fetchExperienceAvailabilityCalendar = (type, identifier, params = {}) => {
  const safeType = normalizeType(type);
  if (safeType === "safari" || safeType === "cruise" || safeType === "trek") {
    const query = buildQueryString(params);
    if (safeType === "trek") {
      if (!params?.slotId) {
        return fetchExperienceAvailability(type, identifier);
      }
      return client.get(`${buildPath(type, identifier, "availability-calendar")}${query}`);
    }
    if (!params?.slotId || !params?.vehicleId) {
      return fetchExperienceAvailability(type, identifier);
    }
    return client.get(`${buildPath(type, identifier, "availability-calendar")}${query}`);
  }
  if (safeType !== "room") {
    return fetchExperienceAvailability(type, identifier);
  }
  const query = buildQueryString(params);
  return client.get(`${buildPath(type, identifier, "availability-calendar")}${query}`);
};

export const createExperienceQuote = (type, identifier, payload) =>
  client.post(
    buildPath(type, identifier, "quote"),
    mergeAffiliateAttributionIntoPayload(payload),
  );

export const createExperiencePaymentOrder = (type, identifier, payload) =>
  client.post(buildPath(type, identifier, "payment-order"), payload);

export const createExperienceBooking = (type, identifier, payload) =>
  client.post(buildPath(type, identifier, "bookings"), payload);

export const fetchExperiencePricing = (type, identifier, payload, options = {}) => {
  const viewerKey = getSavingsViewerKey();
  return client.post(
    buildPath(type, identifier, "pricing"),
    mergeAffiliateAttributionIntoPayload({ ...payload, includeSavingsPreview: true }),
    options,
  ).then((data) => data?.savingsPreview ? { ...data, savingsPreview: { ...data.savingsPreview, viewerKey } } : data);
};


export const fetchExperiencePricingRange = (type, identifier, payload) => {
  const safeType = normalizeType(type);
  if (safeType === "safari" || safeType === "trek") {
    return client.post(buildPath(type, identifier, "pricing-range"), payload);
  }
  return fetchExperiencePricing(type, identifier, payload);
};

export const fetchExperienceBooking = (type, referenceCode) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.get(`${base}/bookings/${referenceCode}`);
};

export const verifyExperiencePayment = (type, referenceCode) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/verify`);
};

export const fetchSafariPolicies = (destinationSlug, experienceSlug, options = {}) => {
  if (!destinationSlug || !experienceSlug) {
    return Promise.reject(new Error("Destination and experience slugs are required"));
  }
  const params = new URLSearchParams();
  if (options.vehicleId) params.set("vehicleId", options.vehicleId);
  const query = params.toString() ? `?${params.toString()}` : "";
  const path = `/explore/experiences/${encodeURIComponent(destinationSlug)}/${encodeURIComponent(experienceSlug)}/policies${query}`;
  return client.get(path);
};

export const fetchExperienceReceiptStatus = (type, referenceCode) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.get(`${base}/bookings/${referenceCode}/receipt`);
};

export const cancelExperienceBooking = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${CANCELLATION_ENDPOINTS[safeType] || ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/cancel`, payload);
};

export const previewExperienceCancellation = (type, referenceCode) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${CANCELLATION_ENDPOINTS[safeType] || ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/cancel/preview`);
};

export const requestCancellationOtp = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${CANCELLATION_ENDPOINTS[safeType] || ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/cancel/otp`, payload);
};

export const confirmExperienceCancellation = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${CANCELLATION_ENDPOINTS[safeType] || ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/cancel`, payload);
};

export const previewExperienceModification = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/modify/preview`, payload);
};

export const requestExperienceModificationOtp = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/modify/otp`, payload);
};

export const createExperienceModificationPaymentOrder = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/modify/payment-order`, payload);
};

export const confirmExperienceModification = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/modify/confirm`, payload);
};

export const createExperienceModificationCheckoutSession = (type, referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(`${base}/bookings/${referenceCode}/modify/session`, payload);
};

export const requestExperienceModificationCheckoutOtp = (
  type,
  referenceCode,
  checkoutReferenceCode,
  payload,
) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  if (!checkoutReferenceCode) return Promise.reject(new Error("Modification checkout reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(
    `${base}/bookings/${referenceCode}/modify/checkout/${checkoutReferenceCode}/otp/request`,
    payload,
  );
};

export const verifyExperienceModificationCheckoutOtp = (
  type,
  referenceCode,
  checkoutReferenceCode,
  payload,
) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  if (!checkoutReferenceCode) return Promise.reject(new Error("Modification checkout reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  const otpPayload = payload?.otp && typeof payload.otp === "object" ? payload.otp : null;
  const normalizedPayload = otpPayload ? { ...payload } : payload;
  if (otpPayload) {
    delete normalizedPayload.otp;
    normalizedPayload.channel = otpPayload.channel ?? payload.channel;
    normalizedPayload.requestId = otpPayload.requestId ?? payload.requestId;
    normalizedPayload.code = otpPayload.code ?? payload.code;
  }
  return client.post(
    `${base}/bookings/${referenceCode}/modify/checkout/${checkoutReferenceCode}/otp/verify`,
    normalizedPayload,
  );
};

export const fetchExperienceModificationCheckoutStatus = (type, referenceCode, checkoutReferenceCode) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  if (!checkoutReferenceCode) return Promise.reject(new Error("Modification checkout reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.get(`${base}/bookings/${referenceCode}/modify/checkout/${checkoutReferenceCode}`);
};

export const createExperienceModificationCheckoutPaymentOrder = (
  type,
  referenceCode,
  checkoutReferenceCode,
  payload,
) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  if (!checkoutReferenceCode) return Promise.reject(new Error("Modification checkout reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(
    `${base}/bookings/${referenceCode}/modify/checkout/${checkoutReferenceCode}/payment-order`,
    payload,
  );
};

export const confirmExperienceModificationCheckout = (
  type,
  referenceCode,
  checkoutReferenceCode,
  payload,
) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  if (!checkoutReferenceCode) return Promise.reject(new Error("Modification checkout reference is required"));
  const safeType = normalizeType(type);
  const base = `/portal/${ENDPOINTS[safeType]}`;
  return client.post(
    `${base}/bookings/${referenceCode}/modify/checkout/${checkoutReferenceCode}/confirm`,
    payload,
  );
};

export const submitRoomWebCheckin = (referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  return client.post(`/portal/${ENDPOINTS.room}/bookings/${referenceCode}/web-checkin`, payload);
};

export const fetchRoomWebCheckin = (referenceCode) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  return client.get(`/portal/${ENDPOINTS.room}/bookings/${referenceCode}/web-checkin`);
};

export const saveRoomWebCheckinDraft = (referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Booking reference is required"));
  return client.put(`/portal/${ENDPOINTS.room}/bookings/${referenceCode}/web-checkin`, payload);
};

const buildExperienceWebCheckinPath = (type, referenceCode) => {
  if (!referenceCode) throw new Error("Booking reference is required");
  const safeType = normalizeWebCheckinType(type);
  const endpoint = WEB_CHECKIN_ENDPOINTS[safeType];
  if (!endpoint) throw new Error("Experience web check-in type is required");
  return `/portal/${endpoint}/bookings/${encodeURIComponent(referenceCode)}/web-checkin`;
};

export const fetchExperienceWebCheckin = (type, referenceCode) =>
  client.get(buildExperienceWebCheckinPath(type, referenceCode));

export const saveExperienceWebCheckinDraft = (type, referenceCode, payload) =>
  client.put(buildExperienceWebCheckinPath(type, referenceCode), payload);

export const submitExperienceWebCheckin = (type, referenceCode, payload) =>
  client.post(buildExperienceWebCheckinPath(type, referenceCode), payload);

export const submitPackageWebCheckin = (referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Package reference is required"));
  return client.post(`/portal/cart/checkout/${encodeURIComponent(referenceCode)}/web-checkin`, payload);
};

export const fetchPackageWebCheckin = (referenceCode) => {
  if (!referenceCode) return Promise.reject(new Error("Package reference is required"));
  return client.get(`/portal/cart/checkout/${encodeURIComponent(referenceCode)}/web-checkin`);
};

export const savePackageWebCheckinDraft = (referenceCode, payload) => {
  if (!referenceCode) return Promise.reject(new Error("Package reference is required"));
  return client.put(`/portal/cart/checkout/${encodeURIComponent(referenceCode)}/web-checkin`, payload);
};
